import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Users, CreditCard, Sparkles, ExternalLink, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trialing: "bg-amber-50 text-amber-700 border-amber-200",
  past_due: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminBilling() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");

  const reload = async () => {
    setLoading(true);
    try { const r = await api.get("/billing/summary"); setS(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  // Poll status if returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    setPolling(true);
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          toast.success("Payment confirmed — subscription active");
          setPolling(false);
          setParams({});
          reload();
          return;
        }
        if (r.data.status === "expired" || attempts > 6) {
          setPolling(false);
          if (r.data.status === "expired") toast.error("Payment session expired");
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        if (!cancelled) { setPolling(false); toast.error("Status check failed"); }
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  const checkout = async () => {
    setActing(true);
    try {
      const r = await api.post("/billing/checkout", { plan_id: s.plan_id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setActing(false); }
  };

  if (loading) return <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading billing…</div>;
  if (!s) return null;

  const subStatus = s.subscription_status || "trialing";
  const periodEnd = s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : null;

  return (
    <div className="max-w-4xl space-y-6" data-testid="billing-page">
      <div>
        <div className="axia-overline">Admin · Billing</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Subscription & seats</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">Per-seat pricing — one seat per active employee. Pay monthly via Stripe.</p>
      </div>

      {polling && (
        <div className="axia-card p-4 flex items-center gap-3 text-sm text-amber-700 border-amber-200" data-testid="payment-polling">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment with Stripe…
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI icon={<Users className="w-4 h-4" />} label="Active seats" value={s.seats} hint="non-separated employees" testid="seats-kpi" />
        <KPI icon={<CreditCard className="w-4 h-4" />} label="Monthly total" value={`$${s.monthly_total.toFixed(2)}`} hint={`$${s.price_per_seat}/seat · ${s.plan_name}`} testid="total-kpi" />
        <KPI icon={<Sparkles className="w-4 h-4" />} label="Status" value={
          <span className={`axia-pill ${STATUS_STYLE[subStatus] || STATUS_STYLE.trialing}`} data-testid="sub-status">{subStatus.replace("_", " ")}</span>
        } hint={periodEnd ? `renews ${periodEnd}` : "no active subscription"} />
      </div>

      <div className="axia-card p-6">
        <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Pay this month</h2>
        <p className="text-sm text-slate-600">Charge ${s.monthly_total.toFixed(2)} for {s.seats} seats on the {s.plan_name} plan. The payment activates a 30-day billing period.</p>
        <div className="mt-4 flex items-center gap-2">
          <Button onClick={checkout} disabled={acting || s.seats === 0} data-testid="checkout-btn">
            {acting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <CreditCard className="w-3.5 h-3.5 mr-2" />}
            Pay ${s.monthly_total.toFixed(2)} via Stripe
          </Button>
          {s.seats === 0 && <span className="text-xs text-amber-700 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Add employees first</span>}
        </div>
      </div>

      <div className="axia-card p-6">
        <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(s.plans || {}).map(([id, plan]) => {
            const active = id === s.plan_id;
            return (
              <div key={id} className={`border rounded-lg p-4 ${active ? "border-slate-900 bg-slate-50" : "border-slate-200"}`} data-testid={`plan-${id}`}>
                <div className="font-display text-base font-semibold text-slate-900">{plan.name}</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">${plan.price_per_seat}<span className="text-sm font-normal text-slate-500"> /seat/mo</span></div>
                <div className="text-xs text-slate-500 mt-2">Min {plan.min_seats} seat{plan.min_seats === 1 ? "" : "s"}</div>
                {active && <div className="mt-3 text-[11px] uppercase tracking-wider text-slate-700 font-semibold">Current plan</div>}
              </div>
            );
          })}
        </div>
      </div>

      {s.last_payment && (
        <div className="axia-card p-6">
          <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Last payment</h2>
          <ul className="text-sm space-y-1 text-slate-700">
            <li><span className="text-slate-500">Amount:</span> ${s.last_payment.amount?.toFixed(2)} {s.last_payment.currency?.toUpperCase()}</li>
            <li><span className="text-slate-500">Status:</span> <span className={`axia-pill ${s.last_payment.payment_status === "paid" ? STATUS_STYLE.active : STATUS_STYLE.trialing}`}>{s.last_payment.payment_status}</span></li>
            <li className="text-xs text-slate-400 font-mono">{s.last_payment.session_id}</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function KPI({ icon, label, value, hint, testid }) {
  return (
    <div className="axia-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500">{icon}<div className="axia-overline">{label}</div></div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}
